<template>
  <benchmark-bar-group
    v-if="loading || props.metrics.length"
    title="Workforce Impact: Enrolled vs Non-Enrolled"
    :has-subtitle="!!props.metrics.length"
    :groups="computedGroups"
    :loading="loading"
  >
    <template #subtitle> Comparing outcomes for enrolled employees versus non-enrolled employees. </template>
  </benchmark-bar-group>
</template>

<script setup>
import { computed } from 'vue';
import { WORKFORCE_IMPACT_GROUPS } from '@/pages/analyticsBenchmark/benchmarkConfig';
import { buildMetricsMap, calculateDelta, formatValue } from '@/pages/analyticsBenchmark/benchmarkUtils';
import BenchmarkBarGroup from '@/pages/analyticsBenchmark/components/BenchmarkBarGroup.vue';

const props = defineProps({
  metrics: { type: Array, default: () => [] },
  sector: { type: String, default: null },
  loading: { type: Boolean, default: false },
});

const metricsMap = computed(() => buildMetricsMap(props.metrics));

const computedGroups = computed(() =>
  WORKFORCE_IMPACT_GROUPS.map(group => ({
    name: group.name,
    metrics: group.metrics.map(config => {
      const enrolledMetric = metricsMap.value[config.enrolledMetricId];
      const enrolledValue = enrolledMetric?.company_value ?? null;
      const unit = enrolledMetric?.unit || 'percent';

      let nonEnrolledValue = null;
      let averageLabel = 'Non-enrolled';
      let formattedAverage;

      if (config.nonEnrolledDisplay === 'dash') {
        formattedAverage = '—';
      } else if (config.nonEnrolledDisplay === 'sector') {
        nonEnrolledValue = enrolledMetric?.sector_value ?? null;
        averageLabel = 'Sector avg';
        formattedAverage = formatValue(nonEnrolledValue, unit);
      } else {
        const nonEnrolledMetric = config.nonEnrolledMetricId ? metricsMap.value[config.nonEnrolledMetricId] : null;
        nonEnrolledValue = nonEnrolledMetric?.company_value ?? null;
        formattedAverage = formatValue(nonEnrolledValue, unit);
      }

      const showGap = config.nonEnrolledDisplay !== 'dash';
      const delta = showGap
        ? calculateDelta(enrolledValue, nonEnrolledValue, {
            deltaFormat: config.deltaFormat,
            polarity: config.polarity,
            unit,
          })
        : { raw: null, formatted: '—', colorClass: 'delta-neutral' };

      const isPercentage = unit === 'percent';
      const maxValue = isPercentage ? 100 : Math.max(0, enrolledValue || 0, nonEnrolledValue || 0) * 1.2 || 100;

      return {
        id: config.enrolledMetricId,
        label: config.label,
        description: enrolledMetric?.description || null,
        tooltipId: `wi-${config.enrolledMetricId}`,
        value: enrolledValue,
        average: nonEnrolledValue,
        maxValue,
        formattedValue: formatValue(enrolledValue, unit),
        formattedAverage,
        deltaFormatted: delta.formatted,
        deltaColorClass: delta.colorClass,
        valueLabel: 'Enrolled',
        averageLabel,
      };
    }),
  })),
);
</script>
